import { useState } from "react";
import { ChevronUp, Timer } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  formatHoldCountdown,
  useGrantNthPurchaseCampaignHold,
  useNthPurchaseCampaignHold,
  type NthPurchaseCampaignHold,
} from "@frontend-common/hooks/use-nth-purchase-campaign";
import { useActiveReservations } from "@/hooks/use-active-reservations";
import { useToken } from "@/hooks/use-token";
import { cn } from "@/lib/utils";

/** A logged-out customer asks too: campaigns that include anonymous purchases
 * give this kiosk a hold of its own. */
const ANONYMOUS_ACCOUNT_KEY = "anonymous";

/** Null (no grant, no hold, no banner) while the user's startable
 * reservations are loading or when they have any: those are already paid, so
 * the campaign must not lock the shop's next start for them. */
function useAccountKey() {
  const token = useToken();
  const activeReservations = useActiveReservations();
  if (!token) {
    return ANONYMOUS_ACCOUNT_KEY;
  }
  if (activeReservations.isPending) {
    return null;
  }
  // a failed reservations lookup shouldn't cost the user the campaign
  return (activeReservations.data?.length ?? 0) > 0 ? null : token;
}

/** The live nth-purchase-campaign hold at this kiosk's shop, of the logged-in
 * user or (logged out) of the kiosk. Null when none, the flag is off, or the
 * kiosk is offline. Shares its query with the banner, so every screen sees the
 * same hold. */
export function useKioskNthPurchaseCampaignHold(shopId: number | null) {
  return useNthPurchaseCampaignHold(shopId, useAccountKey());
}

/** Asks for a hold on the first screen after login, or after choosing to go on
 * without logging in. Renders nothing. */
export function NthPurchaseCampaignGrant({ shopId }: { shopId: number | null }) {
  useGrantNthPurchaseCampaignHold(shopId, useAccountKey());
  return null;
}

/** Floats in from the top while the user holds an nth-purchase-campaign discount.
 * Never blocks the page: only the banner itself takes taps. Tapping folds it
 * into a small chip, tapping the chip unfolds it again. */
export function NthPurchaseCampaignBanner({ shopId }: { shopId: number | null }) {
  const { hold, secondsLeft } = useKioskNthPurchaseCampaignHold(shopId);
  if (!hold) {
    return null;
  }
  // a new hold starts unfolded again
  return (
    <NthPurchaseCampaignBannerView
      key={hold.holdId}
      hold={hold}
      secondsLeft={secondsLeft}
    />
  );
}

export function NthPurchaseCampaignBannerView({
  hold,
  secondsLeft,
}: {
  hold: Pick<NthPurchaseCampaignHold, "percentage" | "title" | "body">;
  secondsLeft: number;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const countdown = formatHoldCountdown(secondsLeft);

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-40 flex px-6 pt-5",
        collapsed ? "justify-end" : "justify-center",
      )}
      data-testid="nth-purchase-campaign-banner"
    >
      {collapsed ? (
        <button
          type="button"
          aria-label={t("Kampány kedvezmény megnyitása")}
          onClick={() => setCollapsed(false)}
          className="pointer-events-auto flex items-center gap-3 rounded-full bg-bub-orange py-3 pl-5 pr-6 shadow-[0_14px_30px_-12px_rgba(244,94,34,0.8)] ring-4 ring-white animate-in fade-in zoom-in-75 duration-300 active:scale-95"
        >
          <span className="font-helvetica text-[30px] font-bold leading-none text-white">
            −{hold.percentage}%
          </span>
          <span className="h-7 w-px bg-white/50" />
          <Timer size={26} color="#ffffff" strokeWidth={2.5} />
          <span className="font-helvetica text-[26px] font-bold leading-none tabular-nums text-white">
            {countdown}
          </span>
        </button>
      ) : (
        <button
          type="button"
          aria-label={t("Kampány kedvezmény összecsukása")}
          onClick={() => setCollapsed(true)}
          className="pointer-events-auto flex w-full max-w-4xl items-center gap-6 rounded-3xl bg-white p-5 pr-7 text-left shadow-[0_24px_50px_-18px_rgba(0,59,85,0.55)] ring-4 ring-bub-orange animate-in fade-in slide-in-from-top-12 duration-500 ease-out active:scale-[0.99]"
        >
          <span className="flex size-28 shrink-0 items-center justify-center rounded-full bg-bub-orange">
            <span className="font-helvetica text-[38px] font-bold leading-none text-white">
              −{hold.percentage}%
            </span>
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="font-helvetica text-[32px] font-bold leading-tight">
              {hold.title}
            </span>
            <span className="text-[22px] leading-snug">{hold.body}</span>
          </span>
          <span className="flex shrink-0 flex-col items-center gap-1">
            <span className="text-[16px] font-semibold uppercase tracking-wide text-bub-orange">
              <Trans>Hátralévő idő</Trans>
            </span>
            <span className="font-helvetica text-[44px] font-bold leading-none tabular-nums text-bub-orange">
              {countdown}
            </span>
            <ChevronUp size={30} color="#005D86" className="mt-1" />
          </span>
        </button>
      )}
    </div>
  );
}
